import { Icon } from '#common/components/Icon';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { calculateTimeBasedProgress, type ProgressState } from '../../../common/config/progress-stages';

interface ProgressBarProps {
    progressState: ProgressState;
    stepTimeoutWarning: string | null;
    progressError: string | null;
    setProgressError: (error: string | null) => void;
    queuePosition: number;
    variant?: 'default' | 'unified';
}

export function ProgressBar({
    progressState,
    stepTimeoutWarning,
    progressError,
    setProgressError,
    queuePosition,
    variant = 'default',
}: ProgressBarProps) {
    const { rawT } = useEditableTranslation('audio-generation');
    const isUnified = variant === 'unified';
    if (progressError) {
        return (
            <div className={isUnified ? 'w-full max-w-xl mx-auto' : 'mt-6 w-full max-w-2xl mx-auto'}>
                <div className={isUnified ? 'core-generation-panel p-5' : 'bg-white rounded-xl p-6 shadow-lg'}>
                    <div className={isUnified ? 'core-generation-error text-center' : 'text-center text-red-600'}>
                        <Icon icon='heroicons--exclamation-triangle' className='w-6 h-6 mx-auto mb-2' />
                        <p>Unable to display progress.</p>
                        <p>{progressError}</p>
                    </div>
                </div>
            </div>
        );
    }

    try {
        const { percentage, currentTotalMs, totalElapsed } = calculateTimeBasedProgress(progressState);
        const currentStep = progressState.steps[progressState.currentStepIndex];
        const isComplete = progressState.currentStepIndex >= progressState.steps.length;
        const displayPercentage = isComplete ? 100 : Math.round(percentage);

        const now = Date.now();

        const elapsedSeconds = Math.round((now - progressState.processStartTime) / 1000);
        const remainingMs = Math.max(0, currentTotalMs - totalElapsed);
        const remainingSeconds = Math.round(remainingMs / 1000);

        const currentStepName = currentStep?.name || 'COMPLETE';
        let stepNumber = progressState.currentStepIndex + 1;
        if (progressState.currentStepIndex === progressState.steps.length) {
            stepNumber = progressState.steps.length;
        }
        const totalSteps = progressState.steps.length;

        let stageText = rawT('step-text', { stepNumber, totalSteps, stepName: currentStepName });
        if (currentStepName === 'IN_QUEUE' && queuePosition > 0) {
            stageText += ` (${rawT('queue-position', { count: queuePosition })})`;
        }
        const timeText = rawT('time-text', { elapsedSeconds, remainingSeconds });

        const progressColor = isComplete ? 'from-green-500 to-green-600' : 'from-blue-500 to-blue-600';

        return (
            <div className={isUnified ? 'w-full max-w-xl mx-auto' : 'w-full max-w-2xl mx-auto'}>
                <div
                    className={isUnified
                        ? 'core-generation-panel p-5 sm:p-6'
                        : 'bg-white rounded-xl p-6 shadow-lg text-black'}
                >
                    {/* Stage and time information */}
                    <div className='mb-3'>
                        <div className='flex justify-between items-center gap-4 mb-1'>
                            <span className={isUnified ? 'text-sm font-bold sm:text-base' : 'text-lg font-semibold'}>
                                {stageText}
                            </span>
                            <span
                                className={isUnified
                                    ? 'core-generation-time text-xs sm:text-sm'
                                    : 'text-sm text-gray-600'}
                            >
                                {timeText}
                            </span>
                        </div>
                    </div>

                    <div
                        className={isUnified
                            ? 'core-generation-track h-2.5 w-full overflow-hidden'
                            : 'w-full bg-gray-200 rounded-full h-4 overflow-hidden mb-3'}
                    >
                        <div
                            className={isUnified
                                ? 'core-generation-progress relative h-full transition-all duration-300 ease-linear'
                                : `h-full bg-gradient-to-r ${progressColor} rounded-full transition-all duration-300 ease-linear relative`}
                            style={{ width: `${displayPercentage}%` }}
                        >
                            {!isComplete && (
                                <div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer' />
                            )}
                        </div>
                    </div>

                    {/* Step timeout warning */}
                    {stepTimeoutWarning && (
                        <div
                            className={isUnified
                                ? 'core-generation-warning mt-4 p-3'
                                : 'mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg'}
                        >
                            <div className='flex items-center'>
                                <Icon
                                    icon='heroicons--exclamation-triangle'
                                    className={isUnified
                                        ? 'mr-2 h-4 w-4 text-[var(--core-yellow)]'
                                        : 'w-4 h-4 text-yellow-600 mr-2'}
                                />
                                <span className={isUnified ? 'text-sm' : 'text-sm text-yellow-800'}>
                                    {stepTimeoutWarning}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    } catch (error) {
        setProgressError(error as string);
    }
}
